import { ICellRendererParams } from '@ag-grid-community/core'
import { Button } from 'components/common'
import Modal from 'components/modal'
import toast from 'react-hot-toast'
import {
  resyncErpAttemptedOrder,
  resyncErpAttemptedRecon,
  resyncErpAttemptedRma,
} from 'utils/api/pendingErpSyncApi'
import { handleError } from 'utils/helpers/handleError'

type Props = {
  type?: 'rma' | 'order' | 'recon'
  isOpen: boolean
  closeModal: () => void
  params?: ICellRendererParams
}

const ConfirmResyncOrderModal = ({
  params,
  closeModal,
  isOpen,
  type = 'order',
}: Props) => {
  const cancelResync = () => {
    closeModal()
  }

  const orderId =
    type === 'order'
      ? params?.data?.order_no
      : type === 'recon'
        ? params?.data?.orderNo
        : params?.data?.rmaNo

  const operation =
    type === 'order' ? 'Order' : type === 'recon' ? 'Recon' : 'Rma'

  const handleResyncOrder = async () => {
    if (!orderId || !params) return

    const resyncToast = toast.loading(`Resyncing ${operation}...`, {
      duration: Infinity,
    })

    try {
      if (type === 'order') {
        await resyncErpAttemptedOrder(orderId)
      } else if (type === 'recon') {
        await resyncErpAttemptedRecon(
          orderId,
          params.data?.reconciliations[0].reconciliationType,
        )
      } else {
        await resyncErpAttemptedRma(orderId)
      }

      // Remove row from grid after successful resync
      const rowNode = params.api.getRowNode(
        type === 'recon' ? params.data._id : orderId,
      )
      params.api.applyTransaction({ remove: [rowNode?.data] })

      toast.success(`${operation} ${orderId} resynced successfully!`, {
        duration: 3000,
        id: resyncToast,
      })

      closeModal()
    } catch (error) {
      handleError(error, resyncToast)
    }
  }

  return (
    <Modal
      width={550}
      title={`Confirm resync ${operation} "${orderId}" ?`}
      titleClassname="text-xl font-semibold max-w-xl"
      isOpen={isOpen}
      closeModal={cancelResync}
      customStyle={{
        padding: '25px 40px',
      }}
    >
      <>
        <div>
          Resyncing {operation} <strong>{orderId}</strong> will re-trigger the
          ERP sync process for this {operation}. Are you sure you want to
          proceed?
        </div>

        {/* Actions */}
        <div className="mt-6 flex w-full items-center gap-5">
          <Button
            onClick={handleResyncOrder}
            className="w-full"
            buttonText="Confirm"
            type="button"
          />
          <Button
            className="w-full"
            buttonText="Cancel"
            variant="outline"
            type="button"
            onClick={cancelResync}
          />
        </div>
      </>
    </Modal>
  )
}

export default ConfirmResyncOrderModal
